// src/hooks/useAnimationExport.js
import { useState, useRef, useCallback, useEffect, useMemo } from "react";
import {
  startExport,
  getExportStatus,
  downloadExport,
} from "../services/export.service.js";

const POLL_INTERVAL_MS = 4000;
const EXPORT_JOB_STORAGE_PREFIX = "animation-export-job";

const normalizeFrame = (frame) => {
  if (!frame) return null;
  return {
    x: Number(frame.x ?? 0),
    y: Number(frame.y ?? 0),
    width: Number(frame.width ?? 0),
    height: Number(frame.height ?? 0),
  };
};

const buildExportSignature = ({
  animationId,
  exportType,
  quality,
  layout,
  startTime,
  endTime,
  time,
  frame,
}) =>
  JSON.stringify({
    animationId,
    exportType,
    quality,
    layout,
    startTime: Number(startTime ?? 0),
    endTime: Number(endTime ?? 0),
    time: Number(time ?? 0),
    frame: normalizeFrame(frame),
  });

const useAnimationExport = ({
  animationId,
  animationTitle = "animation",
  exportType = "video",
  quality = "1080p",
  layout = "landscape",
  startTime = 0,
  endTime = 0,
  time = 0,
  frame = null,
  onProgress,
}) => {
  const [isExporting, setIsExporting] = useState(false);
  const [exportProgress, setExportProgress] = useState(0);
  const [exportError, setExportError] = useState(null);
  const [exportEtaSeconds, setExportEtaSeconds] = useState(null);
  const [exportElapsedSeconds, setExportElapsedSeconds] = useState(0);
  const [exportPhase, setExportPhase] = useState("queued");
  const pollTimerRef = useRef(null);
  const jobIdRef = useRef(null);
  const abortedRef = useRef(false);
  const pollInFlightRef = useRef(false);

  const storageKey = useMemo(
    () => `${EXPORT_JOB_STORAGE_PREFIX}:${animationId || "unknown"}`,
    [animationId],
  );

  const exportSignature = useMemo(
    () =>
      buildExportSignature({
        animationId,
        exportType,
        quality,
        layout,
        startTime,
        endTime,
        time,
        frame,
      }),
    [animationId, exportType, quality, layout, startTime, endTime, time, frame],
  );

  const persistActiveJob = useCallback(
    (jobId) => {
      if (!animationId || !jobId) return;
      localStorage.setItem(
        storageKey,
        JSON.stringify({
          jobId,
          signature: exportSignature,
        }),
      );
    },
    [animationId, exportSignature, storageKey],
  );

  const clearPersistedJob = useCallback(() => {
    localStorage.removeItem(storageKey);
  }, [storageKey]);

  const reportProgress = useCallback(
    (value) => {
      setExportProgress(value);
      onProgress?.(value);
    },
    [onProgress],
  );

  const stopPolling = useCallback(() => {
    if (pollTimerRef.current) {
      clearInterval(pollTimerRef.current);
      pollTimerRef.current = null;
    }
  }, []);

  const pollStatus = useCallback(
    async (jobId) => {
      if (
        abortedRef.current ||
        !jobId ||
        jobIdRef.current !== jobId ||
        pollInFlightRef.current
      ) {
        return;
      }

      pollInFlightRef.current = true;

      try {
        const data = await getExportStatus(jobId);
        if (abortedRef.current || jobIdRef.current !== jobId) return;

        if (!data || !data.jobId) {
          stopPolling();
          clearPersistedJob();
          jobIdRef.current = null;
          setIsExporting(false);
          setExportPhase("idle");
          return;
        }

        reportProgress(data.progress ?? 0);
        setExportEtaSeconds(
          Number.isFinite(data.etaSeconds)
            ? Math.max(0, Math.round(data.etaSeconds))
            : null,
        );
        setExportElapsedSeconds(
          Number.isFinite(data.elapsedSeconds)
            ? Math.max(0, Math.round(data.elapsedSeconds))
            : 0,
        );
        setExportPhase(data.phase || "processing");

        if (data.status === "done") {
          stopPolling();
          clearPersistedJob();
          reportProgress(100);
          // Download directly from server — no Cloudinary URL
          await downloadExport(jobId, animationTitle, {
            exportType,
            quality,
            layout,
            startTime,
            endTime,
            time,
            frame,
          });
          setIsExporting(false);
          jobIdRef.current = null;
        } else if (data.status === "error") {
          stopPolling();
          clearPersistedJob();
          setExportError(data.error || "Export failed on server");
          setIsExporting(false);
          jobIdRef.current = null;
        } else if (
          data.status === "cancelled" ||
          data.status === "downloaded"
        ) {
          stopPolling();
          clearPersistedJob();
          setIsExporting(false);
          setExportEtaSeconds(null);
          setExportElapsedSeconds(0);
          setExportPhase(data.status);
          jobIdRef.current = null;
        }
      } catch (err) {
        const statusCode = err?.statusCode || err?.response?.status;
        if (statusCode === 404) {
          stopPolling();
          clearPersistedJob();
          setIsExporting(false);
          setExportPhase("idle");
          jobIdRef.current = null;
          return;
        }

        stopPolling();
        setExportError(err?.message || "Failed to fetch export progress");
        setIsExporting(false);
        setExportEtaSeconds(null);
        setExportElapsedSeconds(0);
        setExportPhase("error");
        jobIdRef.current = null;
      } finally {
        pollInFlightRef.current = false;
      }
    },
    [
      quality,
      exportType,
      layout,
      startTime,
      endTime,
      time,
      frame,
      animationTitle,
      clearPersistedJob,
      reportProgress,
      stopPolling,
    ],
  );

  const startPolling = useCallback(
    (jobId) => {
      stopPolling();

      if (!jobId || abortedRef.current || jobIdRef.current !== jobId) {
        return;
      }

      pollTimerRef.current = setInterval(() => {
        if (abortedRef.current || jobIdRef.current !== jobId) {
          stopPolling();
          return;
        }

        pollStatus(jobId);
      }, POLL_INTERVAL_MS);
    },
    [pollStatus, stopPolling],
  );

  const exportMedia = useCallback(async () => {
    if (!animationId) {
      setExportError("No animation ID");
      return;
    }

    setIsExporting(true);
    setExportError(null);
    setExportProgress(0);
    setExportEtaSeconds(null);
    setExportElapsedSeconds(0);
    setExportPhase("queued");
    abortedRef.current = false;

    try {
      const { jobId } = await startExport(animationId, {
        exportType,
        quality,
        layout,
        startTime,
        endTime,
        time,
        frame,
      });
      jobIdRef.current = jobId;
      persistActiveJob(jobId);
      reportProgress(1);
      await pollStatus(jobId);

      if (!abortedRef.current && jobIdRef.current === jobId) {
        startPolling(jobId);
      }
    } catch (err) {
      setExportError(err.message || "Failed to start export");
      setIsExporting(false);
    }
  }, [
    animationId,
    exportType,
    quality,
    layout,
    startTime,
    endTime,
    time,
    frame,
    persistActiveJob,
    pollStatus,
    reportProgress,
    startPolling,
  ]);

  const cancelExport = useCallback(() => {
    abortedRef.current = true;
    stopPolling();
    clearPersistedJob();
    setIsExporting(false);
    setExportProgress(0);
    setExportEtaSeconds(null);
    setExportElapsedSeconds(0);
    setExportPhase("cancelled");
    jobIdRef.current = null;
  }, [clearPersistedJob, stopPolling]);

  useEffect(() => {
    if (!animationId) return;

    const raw = localStorage.getItem(storageKey);
    if (!raw) return;

    let ignore = false;

    try {
      const parsed = JSON.parse(raw);
      if (!parsed?.jobId || parsed.signature !== exportSignature) {
        return;
      }

      abortedRef.current = false;
      jobIdRef.current = parsed.jobId;
      setIsExporting(true);
      setExportError(null);

      const resumePolling = async () => {
        await pollStatus(parsed.jobId);

        if (ignore) return;
        if (!abortedRef.current && jobIdRef.current === parsed.jobId) {
          startPolling(parsed.jobId);
        }
      };

      resumePolling();
    } catch {
      clearPersistedJob();
    }

    return () => {
      ignore = true;
      stopPolling();
    };
  }, [
    animationId,
    clearPersistedJob,
    exportSignature,
    pollStatus,
    startPolling,
    stopPolling,
    storageKey,
  ]);

  return {
    exportMedia,
    cancelExport,
    isExporting,
    exportProgress,
    exportEtaSeconds,
    exportElapsedSeconds,
    exportPhase,
    exportError,
  };
};

export default useAnimationExport;
